import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Utensils, ChefHat, ShoppingCart, ClipboardCheck, Scale, Droplets, TrendingUp, Calendar, SlidersHorizontal, Camera, MessageCircle, Pizza, Smartphone, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Navbar from '@/components/Navbar';

const sections = [
  { id: 'meal-plan', label: 'Meal plan', icon: Utensils },
  { id: 'cooking-meal-prep', label: 'Cooking', icon: ChefHat },
  { id: 'shopping-list', label: 'Shopping', icon: ShoppingCart },
  { id: 'logging-meals', label: 'Logging', icon: ClipboardCheck },
  { id: 'weighing-food', label: 'Weighing', icon: Scale },
  { id: 'sauces-extras', label: 'Extras', icon: Droplets },
  { id: 'free-meals', label: 'Free meals', icon: Pizza },
  { id: 'weight-tracking', label: 'Weight', icon: TrendingUp },
  { id: 'weekly-checkins', label: 'Check-ins', icon: Calendar },
  { id: 'adjustments', label: 'Adjustments', icon: SlidersHorizontal },
  { id: 'progress', label: 'Progress', icon: Camera },
  { id: 'support', label: 'Support', icon: MessageCircle },
  { id: 'add-to-home', label: 'Add to phone', icon: Smartphone },
];

export default function HowItWorks({ user, onLogout }) {
  const [showAddToPhone, setShowAddToPhone] = useState(false);
  
  const scrollToSection = (id) => {
    if (id === 'add-to-home') {
      setShowAddToPhone(true);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back link */}
        <Link 
          to="/app" 
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to dashboard
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">How MealGuide Works</h1>
          <p className="text-gray-600">Quick guide to meals, logging, and weekly check-ins.</p>
        </div>

        {/* Jump links */}
        <div className="flex flex-wrap gap-2 mb-10">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-full text-gray-600 hover:border-[#1a61e8] hover:text-[#1a61e8] hover:bg-blue-50/50 transition-all"
              >
                <Icon className="h-3.5 w-3.5" />
                {section.label}
              </button>
            );
          })}
        </div>

        {/* Content sections */}
        <div className="space-y-6">
          
          {/* Your Meal Plan */}
          <section id="meal-plan" className="scroll-mt-24 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#1a61e8]/10 rounded-lg flex items-center justify-center">
                <Utensils className="h-5 w-5 text-[#1a61e8]" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Your Meal Plan</h2>
            </div>
            <div className="space-y-3 text-gray-700">
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-[#1a61e8] rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">1</span>
                <p>Your plan is organised by meals (breakfast, lunch, dinner, snacks)</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-[#1a61e8] rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">2</span>
                <p>Each meal has <strong>3 recipe options</strong> — choose 1 per meal</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-[#1a61e8] rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">3</span>
                <p>Use <strong>Swap</strong> anytime to change a recipe</p>
              </div>
              <p className="text-gray-500 text-sm mt-2 pl-9">You don't need to eat the same meals every day</p>
            </div>
          </section>

          {/* Cooking & Meal Prep */}
          <section id="cooking-meal-prep" className="scroll-mt-24 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <ChefHat className="h-5 w-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Cooking & Meal Prep</h2>
            </div>
            <div className="grid gap-3">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-lg">🍳</span>
                <p className="text-gray-700">Cook fresh or meal prep, both work</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-lg">📦</span>
                <p className="text-gray-700">Increase servings inside a recipe when batch cooking</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-lg">✨</span>
                <p className="text-gray-700">Portions update automatically when servings change</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-lg">🏷️</span>
                <p className="text-gray-700">Do not obsess over food item brands</p>
              </div>
            </div>
          </section>

          {/* Shopping List */}
          <section id="shopping-list" className="scroll-mt-24 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="h-5 w-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Shopping List</h2>
            </div>
            <div className="space-y-2 text-gray-700">
              <p>→ Select the recipes you plan to eat</p>
              <p>→ The <strong>Shopping List</strong> button is located at the top</p>
              <p>→ It updates based on your selected recipes</p>
            </div>
          </section>

          {/* Logging Meals */}
          <section id="logging-meals" className="scroll-mt-24 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <ClipboardCheck className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Logging Meals</h2>
            </div>
            <div className="space-y-2 text-gray-700">
              <p>→ Log <strong>one recipe per meal</strong></p>
              <p>→ Logged meals update your daily total at the top</p>
              <p className="text-gray-500 text-sm">Logging helps accuracy, but it doesn't need to be perfect</p>
            </div>
          </section>

          {/* Weighing Food */}
          <section id="weighing-food" className="scroll-mt-24 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 shadow-sm border border-amber-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <Scale className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Weighing Food</h2>
                <span className="text-xs font-medium text-amber-700 bg-amber-200 px-2 py-0.5 rounded-full">Important</span>
              </div>
            </div>
            <p className="text-gray-800 font-medium mb-4">Weigh food items raw and uncooked</p>
            <div className="grid gap-2">
              <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-amber-100">
                <span className="text-lg">🧊</span>
                <p className="text-gray-700">Weigh frozen foods <strong>after defrosting</strong></p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-amber-100">
                <span className="text-lg">🥫</span>
                <p className="text-gray-700">Weigh foods in liquid (e.g. canned beans) <strong>after draining and rinsing</strong></p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-amber-100">
                <span className="text-lg">⚖️</span>
                <p className="text-gray-700">Use <strong>grams (g/ml)</strong> when listed</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-amber-100">
                <span className="text-lg">🥬</span>
                <p className="text-gray-700">Vegetables don't need strict weighing.</p>
              </div>
            </div>
          </section>

          {/* Sauces, Oils & Extras */}
          <section id="sauces-extras" className="scroll-mt-24 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                <Droplets className="h-5 w-5 text-pink-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Sauces, Oils & Extras</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <p className="text-gray-700">Herbs and spices are <strong>free</strong></p>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                <p className="text-gray-700">Sauces and oils aren't included as ingredients</p>
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-800 mb-2">If you add sauces or oils:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Use lightly</li>
                  <li>• Reduce your carb source by about <strong>10–20g</strong></li>
                  <li>• No need to log sauces separately</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Free Meals */}
          <section id="free-meals" className="scroll-mt-24 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 shadow-sm border border-orange-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <Pizza className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Free Meals or Foods Outside the Plan</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg border border-orange-100">
                <span className="text-lg flex-shrink-0">⚠️</span>
                <p className="text-gray-700">Any food eaten <strong>outside your meal plan</strong> will affect your progress if it's added on top of your planned meals. Yes, fruit included.</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg border border-orange-100">
                <span className="text-lg flex-shrink-0">🍕</span>
                <p className="text-gray-700">You can have <strong>1 free meal per week.</strong></p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg border border-orange-100">
                <span className="text-lg flex-shrink-0">🔄</span>
                <p className="text-gray-700">Replace it with <strong>one meal from your plan</strong>, or <strong>two meals</strong> if it's a large free meal (pizza, burgers, takeaway, etc).</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg border border-orange-100">
                <span className="text-lg flex-shrink-0">✅</span>
                <p className="text-gray-700">Keep it simple, avoid unnecessary extras, choose diet drinks where possible, and get back on plan at your next meal.</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white/70 rounded-lg border border-orange-100">
                <span className="text-lg flex-shrink-0">🥤</span>
                <p className="text-gray-700">Zero-calorie drinks <strong>(around 2 kcal per 100 ml)</strong> are fine in reasonable amounts at any time.</p>
              </div>
            </div>
          </section>

          {/* Weight Tracking */}
          <section id="weight-tracking" className="scroll-mt-24 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-cyan-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Weight Tracking</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-2xl mb-1">📅</p>
                <p className="text-sm text-gray-700">Log daily after you wake up or occasionally</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-2xl mb-1">✅</p>
                <p className="text-sm text-gray-700">Daily recommended, not required</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-2xl mb-1">😌</p>
                <p className="text-sm text-gray-700">Missing a day is fine</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-2xl mb-1">📈</p>
                <p className="text-sm text-gray-700"><strong>Weekly trends</strong> matter most</p>
              </div>
            </div>
          </section>

          {/* Weekly Check-Ins */}
          <section id="weekly-checkins" className="scroll-mt-24 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#1a61e8] rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Weekly Check-Ins</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-blue-100">
                <span className="text-lg">📅</span>
                <p className="text-gray-700">Check-ins are open from 00:00 Wednesday until 23:59 Thursday (CET)</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-blue-100">
                <span className="text-lg">🔒</span>
                <p className="text-gray-700">They lock after that until the next week</p>
              </div>
              <p className="text-sm text-gray-600 mt-2">This keeps your routine consistent and allows proper weekly reviews. Always complete your weekly check-in. <strong>No check-in completed means no adjustments.</strong></p>
            </div>
          </section>

          {/* Adjustments */}
          <section id="adjustments" className="scroll-mt-24 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <SlidersHorizontal className="h-5 w-5 text-violet-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Adjustments</h2>
            </div>
            <div className="space-y-2 text-gray-700">
              <p>→ Based on <strong>trends</strong>, not single days</p>
              <p>→ Changes are <strong>small and gradual</strong></p>
              <p className="text-gray-500 text-sm">If no changes are made, it usually means things are working or your adherence is not high enough to justify one.</p>
            </div>
          </section>

          {/* Progress */}
          <section id="progress" className="scroll-mt-24 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                <Camera className="h-5 w-5 text-rose-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Progress</h2>
            </div>
            <p className="text-gray-700 mb-3">Track more than just the scale:</p>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-700">⚖️ Weight</span>
              <span className="px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-700">📸 Photos</span>
              <span className="px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-700">📏 Measurements</span>
            </div>
          </section>

          {/* Support */}
          <section id="support" className="scroll-mt-24 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <MessageCircle className="h-5 w-5 text-teal-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Support</h2>
            </div>
            <p className="text-gray-500 text-sm mb-3">This is not instant 1:1 chat coaching.</p>
            <p className="text-gray-700 mb-2">Use the message function for:</p>
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-sm text-teal-700">App/tech issues</span>
              <span className="px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-sm text-teal-700">Billing</span>
              <span className="px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-sm text-teal-700">Recipe questions</span>
              <span className="px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-sm text-teal-700">General guidance</span>
            </div>
            <p className="text-gray-500 text-sm">Replies aren&apos;t instant, but messages are reviewed regularly.</p>
          </section>

          {/* Add to Phone - Clickable Card */}
          <section 
            id="add-to-home" 
            className="scroll-mt-24 bg-white rounded-xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md hover:border-green-200 transition-all"
            onClick={() => setShowAddToPhone(true)}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Smartphone className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Add to Your Phone</h2>
                <p className="text-gray-600 text-sm">Get quick access from your home screen</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
          </section>

          {/* Final Note */}
          <section className="scroll-mt-24 bg-gradient-to-r from-[#1a61e8] to-[#4a8ff5] rounded-xl p-6 shadow-lg">
            <p className="text-white text-lg font-medium text-center">
              Consistency matters more than perfection.<br />
              <span className="text-blue-100 text-base font-normal">Bad weeks happen. The plan adjusts over time.</span>
            </p>
          </section>

        </div>
      </div>

      {/* Add to Phone Dialog */}
      <Dialog open={showAddToPhone} onOpenChange={setShowAddToPhone}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <Smartphone className="h-6 w-6 text-green-600" />
              Add to Your Phone
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-gray-600 mb-4">Get quick access to MealGuide from your home screen!</p>
          
          {/* Step 1 */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <h3 className="font-semibold text-gray-900">Tap the Share button</h3>
            </div>
            <p className="text-gray-600 text-sm mb-3 ml-8">In Safari, tap the share icon at the bottom.</p>
            <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm ml-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_mealguide-streak/artifacts/o10d1rhs_1.png" 
                alt="Step 1: Tap the share button"
                className="w-full"
              />
            </div>
          </div>

          {/* Step 2 */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <h3 className="font-semibold text-gray-900">Select &quot;Add to Home Screen&quot;</h3>
            </div>
            <p className="text-gray-600 text-sm mb-3 ml-8">Scroll down and tap the option.</p>
            <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm ml-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_mealguide-streak/artifacts/yr50mhga_3.png" 
                alt="Step 2: Select Add to Home Screen"
                className="w-full"
              />
            </div>
          </div>

          {/* Step 3 */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <h3 className="font-semibold text-gray-900">Done!</h3>
            </div>
            <p className="text-gray-600 text-sm mb-3 ml-8">MealGuide is now on your home screen.</p>
            <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm ml-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_mealguide-streak/artifacts/h16g5grh_2.png" 
                alt="Step 3: MealGuide on home screen"
                className="w-full"
              />
            </div>
          </div>

          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-green-800 text-sm text-center">
              MealGuide will appear on your home screen just like a regular app!
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
